import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { classNames, responsiveClassNames, responsiveVariables } from "../../utilities/props.js";
import Divider from "../Divider/index.js";
import Hidden from "../Hidden/index.js";
import s from "./View.module.css";
import getRadiusStyles from "../../styles/radius/index.js";
import getBleedStyles from "../../styles/bleed/index.js";
import getWidthStyles from "../../styles/width/index.js";
import getHeightStyles from "../../styles/height/index.js";
import getMaxWidthStyles from "../../styles/maxWidth/index.js";
import getMaxHeightStyles from "../../styles/maxHeight/index.js";
import getMinWidthStyles from "../../styles/minWidth/index.js";
import getMinHeightStyles from "../../styles/minHeight/index.js";
import getPositionStyles from "../../styles/position/index.js";
import getInsetStyles from "../../styles/inset/index.js";
import getAspectRatioStyles from "../../styles/aspectRatio/index.js";
import getBorderStyles from "../../styles/border/index.js";
import getTextAlignStyles from "../../styles/textAlign/index.js";
import getAlignStyles from "../../styles/align/index.js";
import getJustifyStyles from "../../styles/justify/index.js";
const ViewItem = (props) => {
    const { columns, grow, gapBefore, 
    // Using any here to let TS save on type resolving, otherwise TS throws an error due to the type complexity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as: TagName = "div", order, children, className, attributes, } = props;
    const itemClassNames = classNames(s.item, className, gapBefore === "auto" && s["item--gap-auto"], gapBefore !== undefined && s["item--gap-before"], columns && s["item--columns"], ...responsiveClassNames(s, "item--grow", grow), ...responsiveClassNames(s, "item--columns", columns));
    const itemVariables = {
        ...responsiveVariables("--rs-view-item-order", order),
        ...responsiveVariables("--rs-view-item-gap-before", gapBefore),
    };
    return (_jsx(TagName, { ...attributes, style: { ...attributes?.style, ...itemVariables }, className: itemClassNames, children: children }));
};
const View = (props) => {
    const { 
    /**
     * Layout props
     */
    align, justify, wrap, gap, height, width, aspectRatio, maxHeight, maxWidth, minHeight, minWidth, padding, paddingInline, paddingBlock, paddingBottom, paddingEnd, paddingStart, paddingTop, bleed, 
    /**
     * Style props
     * */
    animated, backgroundColor, borderColor, borderRadius, shadow, textAlign, overflow, position, inset, insetTop, insetBottom, insetStart, insetEnd, zIndex, 
    /**
     * Item prop
     */
    grow, 
    // Using any here to let TS save on type resolving, otherwise TS throws an error due to the type complexity
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as: TagName = "div", children, divided, className, attributes, } = props;
    let isFlex = !!align || !!justify || !!gap || !!props.direction;
    const direction = props.direction || (isFlex ? "column" : undefined);
    const radiusStyles = getRadiusStyles(borderRadius);
    const bleedStyles = getBleedStyles(bleed);
    const widthStyles = getWidthStyles(width);
    const heightStyles = getHeightStyles(height);
    const maxWidthStyles = getMaxWidthStyles(maxWidth);
    const maxHeightStyles = getMaxHeightStyles(maxHeight);
    const minWidthStyles = getMinWidthStyles(minWidth);
    const minHeightStyles = getMinHeightStyles(minHeight);
    const positionStyles = getPositionStyles(position);
    const insetStyles = getInsetStyles(inset);
    const insetTopStyles = getInsetStyles(insetTop, "top");
    const insetBottomStyles = getInsetStyles(insetBottom, "bottom");
    const insetStartStyles = getInsetStyles(insetStart, "start");
    const insetEndStyles = getInsetStyles(insetEnd, "end");
    const aspectRatioStyles = getAspectRatioStyles(aspectRatio);
    const borderStyles = getBorderStyles(borderColor);
    const textAlignStyles = getTextAlignStyles(textAlign);
    const alignStyles = getAlignStyles(align);
    const justifyStyles = getJustifyStyles(justify);
    let renderedItemIndex = 0;
    // If wrap is not defined, it can be set based on item grow and split usage
    let nowrap;
    const renderDivider = ({ className, key }) => {
        const dividerClassName = classNames(s.divider, className);
        let isDividerVertical = false;
        if (typeof direction === "string" && direction.startsWith("row")) {
            isDividerVertical = true;
        }
        else if (direction) {
            const viewports = Object.keys(direction);
            isDividerVertical = viewports.reduce((acc, viewport) => {
                const viewportDirection = direction[viewport];
                if (!viewportDirection)
                    return acc;
                return {
                    ...acc,
                    [viewport]: viewportDirection.startsWith("row"),
                };
            }, {});
        }
        return (_jsx("div", { className: dividerClassName, children: _jsx(Divider, { vertical: isDividerVertical, blank: true }) }, `${key}-divider`));
    };
    const renderItem = ({ className, child, index }) => {
        const isItem = child.type === ViewItem;
        const isView = child.type === View;
        const key = child.key || index;
        const dividerElement = !!index && divided && renderDivider({ className, key });
        let itemElement;
        if (isItem) {
            itemElement = React.cloneElement(child, {
                className: classNames(className, child.props.className),
            });
        }
        else if (!className &&
            (React.isValidElement(child) ||
                React.Children.count(children === 1) ||
                typeof child === "string")) {
            itemElement = child;
        }
        else {
            itemElement = (_jsx("div", { className: className, children: child }, key));
        }
        // Passing grow here because it's responsive and nowrap should follow it
        if (isItem && child.props?.grow)
            nowrap = child.props.grow;
        if (isItem && child.props?.gap === "auto")
            nowrap = true;
        if ((isItem || isView) && child.props?.grow)
            isFlex = true;
        return (_jsxs(React.Fragment, { children: [dividerElement, itemElement] }, `${key}-fragment`));
    };
    // Using any in favor of resolving the props in runtime where we don't know their props definitions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedChildren = React.Children.map(children, (child, index) => {
        if (!child)
            return null;
        const usedIndex = renderedItemIndex;
        renderedItemIndex += 1;
        if (child.type === Hidden) {
            const { children: hiddenChild, ...hiddenProps } = child.props;
            const key = child.key || index;
            return (_createElement(Hidden, { ...hiddenProps, key: key }, renderItem({ child: hiddenChild, index: usedIndex })));
        }
        if (child.type === React.Fragment && React.Children.count(child.props.children) > 1) {
            // Using any in favor of resolving the props in runtime where we don't know their props definitions
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return child.props.children.map((child) => {
                if (!child)
                    return null;
                renderedItemIndex += 1;
                return renderItem({ child, index: renderedItemIndex });
            });
        }
        return renderItem({ child, index: usedIndex });
    });
    // Classnames and attributes are written here so we can assign nowrap to the root element based on the children
    const rootClassNames = classNames(s.root, className, radiusStyles?.classNames, bleedStyles?.classNames, widthStyles?.classNames, heightStyles?.classNames, maxWidthStyles?.classNames, maxHeightStyles?.classNames, minWidthStyles?.classNames, minHeightStyles?.classNames, borderStyles?.classNames, backgroundColor && s[`--bg-${backgroundColor}`], shadow && s[`--shadow-${shadow}`], overflow && s[`--overflow-${overflow}`], animated && s["--animated"], divided && s["--divided"], (padding !== undefined || paddingInline !== undefined || paddingBlock !== undefined) &&
        s["--padding"], (isFlex || nowrap) && s["--flex"], ...responsiveClassNames(s, "--direction", direction), 
    // Wrap and nowrap are separate here because inverting any of them could result into a false value which will be ignored by classNames
    ...responsiveClassNames(s, "--nowrap", nowrap || wrap === false), ...responsiveClassNames(s, "--wrap", wrap), 
    // Item classnames
    ...responsiveClassNames(s, "item--grow", grow));
    const rootVariables = {
        ...attributes?.style,
        ...responsiveVariables("--rs-view-gap", gap),
        ...responsiveVariables("--rs-view-p-vertical", paddingBlock || padding),
        ...responsiveVariables("--rs-view-p-horizontal", paddingInline || padding),
        ...responsiveVariables("--rs-view-p-bottom", paddingBottom),
        ...responsiveVariables("--rs-view-p-top", paddingTop),
        ...responsiveVariables("--rs-view-p-start", paddingStart),
        ...responsiveVariables("--rs-view-p-end", paddingEnd),
        ...bleedStyles?.variables,
        ...widthStyles?.variables,
        ...heightStyles?.variables,
        ...aspectRatioStyles?.variables,
        ...maxWidthStyles?.variables,
        ...maxHeightStyles?.variables,
        ...minWidthStyles?.variables,
        ...minHeightStyles?.variables,
        ...insetStyles?.variables,
        ...insetTopStyles?.variables,
        ...insetBottomStyles?.variables,
        ...insetStartStyles?.variables,
        ...insetEndStyles?.variables,
        ...alignStyles?.variables,
        ...justifyStyles?.variables,
        ...aspectRatioStyles?.variables,
        ...positionStyles?.variables,
        ...textAlignStyles?.variables,
        ...(zIndex ? { "--rs-view-z": zIndex } : {}),
    };
    return (_jsx(TagName, { ...attributes, className: rootClassNames, style: rootVariables, children: formattedChildren }));
};
View.Item = ViewItem;
View.displayName = "View";
ViewItem.displayName = "View.Item";
export default View;
